/**
 * Parse a Videotap markdown clip link into title + start/end timestamps.
 *
 * Example:
 * [El Sueño Del McDonald's De $1.5 Millones38:02 - 38:53](https://videotap.com/...)
 */

/** mm:ss or hh:mm:ss — prefer hh:mm:ss when both could match */
const TIMESTAMP = String.raw`(?:\d{1,2}:\d{2}:\d{2}|\d{1,2}:\d{2})`;

/** Title + start + separator + end (title may have trailing spaces; trimmed later) */
const LABEL_PATTERN = new RegExp(
  `^(.+?)(${TIMESTAMP})\\s*-\\s*(${TIMESTAMP})$`
);

/**
 * Normalize clipboard / textarea quirks:
 * BOM, zero-width chars, NBSP, en/em/minus dashes, and collapse all whitespace
 * (including newlines from soft-wrapped pastes) to single spaces.
 */
export function normalizeVideotapClipText(raw) {
  return String(raw ?? '')
    .replace(/^\uFEFF/, '')
    .replace(/[\u200B-\u200D\u2060\uFEFF]/g, '')
    .replace(/\u00A0/g, ' ')
    .replace(/[\u2013\u2014\u2212]/g, '-') // en-dash, em-dash, minus
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Pull the "title + timestamps" chunk from a markdown link or bare label.
 * URL in parentheses is ignored.
 */
function extractTitleTimestampsChunk(text) {
  // [label](url) — allow optional space before '('
  const fullLink = text.match(/^\[([\s\S]*)]\s*\([^)]*\)\s*$/);
  if (fullLink) return fullLink[1].trim();

  // Link embedded in surrounding clipboard junk
  const embedded = text.match(/\[([\s\S]*?)]\s*\([^)]*\)/);
  if (embedded) return embedded[1].trim();

  // Bare "Title00:00 - 00:00" (no markdown wrapper)
  return text.trim();
}

/**
 * @param {string} raw
 * @returns {{ title: string, start: string, end: string } | null}
 */
export function parseVideotapClipLink(raw) {
  const text = normalizeVideotapClipText(raw);
  if (!text) return null;

  const chunk = extractTitleTimestampsChunk(text);
  if (!chunk) return null;

  const labelMatch = chunk.match(LABEL_PATTERN);
  if (!labelMatch) return null;

  const title = labelMatch[1].trim();
  const start = labelMatch[2];
  const end = labelMatch[3];

  if (!title || !start || !end) return null;

  return { title, start, end };
}
